import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { getShareGrant } from '../api/sharing';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';
import { SecureText } from '../components/SecureText';

const parsePlaintext = (value) => {
  if (!value) {
    return {};
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return {};
    }
  }

  return value;
};

export default function SharedGrant() {
  const { grantId } = useParams();
  const [searchParams] = useSearchParams();
  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);
  const [grant, setGrant] = useState(null);
  const [plaintext, setPlaintext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadGrant = async () => {
      try {
        const data = await getShareGrant(grantId);
        setGrant(data);

        if (data.status !== 'ACTIVE') {
          setPlaintext(null);
          return;
        }

        const token = searchParams.get('token') || data.encryptedItemKey?.shareToken || data.encryptedItemKey?.token;
        if (!token) {
          throw new Error('Missing share token');
        }

        const itemKey = await vaultCrypto.unwrapItemKey(data.encryptedItemKey, token, data.id, 'share-grant');
        const decrypted = await WebCryptoProvider.decryptAESGCM(
          itemKey,
          data.encryptedData.iv,
          data.encryptedData.ct,
          data.encryptedData.mac,
          `vault-item:${data.vault_item_id}`,
        );

        setPlaintext(parsePlaintext(decrypted));
      } catch (loadError) {
        setError(loadError?.response?.data?.message || loadError.message || 'Unable to open shared item');
      } finally {
        setLoading(false);
      }
    };

    loadGrant();
  }, [grantId, searchParams, vaultCrypto]);

  return (
    <MainLayout>
      <div style={{ maxWidth: '960px', margin: '0 auto', display: 'grid', gap: '20px' }}>
        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <h1 style={{ marginBottom: '8px' }}>Shared Vault Item</h1>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Securely opened from a time-limited share grant.</p>
            </div>
            <Link to="/vault" style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>Back to vault</Link>
          </div>
        </div>

        {loading ? (
          <div className="glass-panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading shared item...</div>
        ) : error ? (
          <div className="glass-panel" style={{ padding: '24px', color: 'var(--danger)' }}>{error}</div>
        ) : grant ? (
          <div className="glass-panel" style={{ padding: '24px', display: 'grid', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>{plaintext?.title || 'Shared Item'}</div>
              <div style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)' }}>
                {grant.status}
              </div>
            </div>

            {grant.status !== 'ACTIVE' ? (
              <div style={{ color: 'var(--text-secondary)' }}>
                This share is {grant.status.toLowerCase()}. The owner's original item remains safe and untouched.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {plaintext?.username && (
                    <div>
                      <div className="input-label">Username</div>
                      <div>{plaintext.username}</div>
                    </div>
                  )}

                  {plaintext?.url && (
                    <div>
                      <div className="input-label">URL</div>
                      <a href={plaintext.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-primary)' }}>
                        {plaintext.url}
                      </a>
                    </div>
                  )}

                  {plaintext?.password && (
                    <SecureText label="Password" value={plaintext.password} />
                  )}

                  {plaintext?.notes && (
                    <div>
                      <div className="input-label">Notes</div>
                      <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>{plaintext.notes}</div>
                    </div>
                  )}
                </div>

                {!plaintext && <div style={{ color: 'var(--text-secondary)' }}>No decrypted content available.</div>}
              </>
            )}
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
}
